import type { ReactNode } from "react";

// Unified badge component. Every tag / chip / status pill that previously
// shipped its own ad-hoc styling goes through this. Two sizes, a handful
// of tones — never wraps, never surprises.

export type BadgeTone =
  | "neutral"
  | "accent"
  | "high"
  | "medium"
  | "low"
  | "success"
  | "warning"
  | "danger"
  | "info";

export type BadgeSize = "standard" | "compact";

interface BadgeProps {
  tone?: BadgeTone;
  size?: BadgeSize;
  title?: string;
  children: ReactNode;
  className?: string;
  // Decorative icon shown before the label. Passed as a string (emoji)
  // or a tiny element. Never a block-level child.
  icon?: ReactNode;
}

export function Badge({
  tone = "neutral",
  size = "standard",
  title,
  children,
  className,
  icon,
}: BadgeProps) {
  const cls = [
    "ui-badge",
    `ui-badge-tone-${tone}`,
    `ui-badge-size-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <span className={cls} title={title}>
      {icon !== undefined && (
        <span className="ui-badge-icon" aria-hidden>
          {icon}
        </span>
      )}
      <span className="ui-badge-label">{children}</span>
    </span>
  );
}
